import { ClockClockwiseIcon, ShieldCheckIcon, WifiSlashIcon } from '@phosphor-icons/react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Chip } from '@/components/common/chip';
import { MetricIndicator } from '@/components/common/metric-indicator';
import { Tooltip } from '@/components/common/tooltip';
import { RegionError } from '@/components/feedback/error-state';
import { routes } from '@/config/routes';
import { useOnlineStatus } from '@/hooks/use-online-status';

import { DEVICE_STATUS } from '../constants';
import { useDeviceMapPopup, useLiveTelemetry } from '../map-popup-hooks';
import {
  METRIC_DASH,
  METRIC_SLOTS,
  OFFLINE_METRICS,
  lastSyncLine,
  liveAnnouncement,
  metricIndicatorFor,
  quickViewChipStatus,
  quickViewIdentity,
  showsSubscriptionBadge,
  splitReading,
  type QuickViewLiveState,
} from '../map-popup-rules';
import type { MapDevice } from '../map-schemas';

import styles from './device-quick-view.module.scss';
import { DEVICE_STATUS_CHIP } from './device-status-chip';

/**
 * The panel's nominal size, the input `placeMapPanel` needs before layout: 405 × 309 (Figma
 * `864:558014`).
 *
 * ponytail: nominal, not measured — the same trade-off `FIELD_PANEL_SIZE` takes. A name long enough
 * to wrap makes the real panel a line taller and shifts the placement by that much; measure the
 * rendered element and feed it back if that is ever seen.
 */
export const DEVICE_QUICK_VIEW_SIZE = { width: 405, height: 309 } as const;

/** FE-drafted, in the house `We couldn't load your <thing>. Please try again.` voice — no Figma
 *  frame covers it. Names the readings specifically: the Device itself is on screen and fine. */
export const METRICS_ERROR = "We couldn't load this device's readings. Please try again.";

export type DeviceQuickViewProps = {
  /** The Device as the marker layer has it. Identity and the opening status come from here, so the
   *  panel is populated before the first byte of the live half — and still populated if it never
   *  arrives (F13). */
  device: MapDevice;
};

/**
 * The **Device quick view** (Figma `823:702499`, panel `864:558014`, leaves `864:558015`–`864:558311`).
 *
 * **No close control**, by criterion (113588 §3a): it is dismissed through the map or Escape, both of
 * which are `<MapPage>`'s `selectionReducer` and neither of which is this component's business. Nor
 * is placement — `<MapAnchoredPanel>` owns where it goes, whether it traps focus, and that it holds
 * the side it opened on.
 *
 * **Two sources, deliberately.** Identity and the opening status come from the marker payload the
 * caller already has; the badge, the readings and the last sync come from `map-popup`. That split is
 * what makes F13 survivable: 8 of the 28 markers the deployed API returns resolve on no per-device
 * route, and this panel opens for them with its header, identity and pill intact and a retryable
 * notice where the readings would be — the marker is **not** hidden (113587 §5b, 113586 §15b).
 *
 * **Live, from one source.** `useLiveTelemetry` mirrors the `telemetry` channel into the very query
 * `useDeviceMapPopup` reads, so a live reading and a fetched one cannot disagree and a refresh is a
 * text change rather than a remount — which is §9a–§9c without this component doing anything about
 * it. The channel is dark until BE publishes a contract, in which case the values simply freeze:
 * exactly §9d.
 *
 * **Nothing here is computed.** Values arrive formatted and states arrive thresholded (§17a); the
 * labels, the dash, the indicator-visibility rule and the last-sync wording are all
 * `../map-popup-rules`, and the last-sync formatter is the Device Details page's own.
 *
 * Strictly read-only (§12a, §12b): the one control is View More, and it is a `<button>` rather than a
 * link because §11e says navigation is programmatic and no new tab is offered.
 */
export function DeviceQuickView({ device }: DeviceQuickViewProps) {
  const navigate = useNavigate();
  const isOnline = useOnlineStatus();
  const { data, isError, refetch } = useDeviceMapPopup(device.id);
  useLiveTelemetry(device.id);

  const identity = quickViewIdentity(
    data ?? { customName: device.deviceCustomName, deviceName: device.deviceName },
  );
  const status = quickViewChipStatus(
    data?.effectiveStatus ?? device.status,
    data?.subscriptionIndicator ?? 'none',
  );
  const chip = DEVICE_STATUS_CHIP[status];
  const isDisconnected = status === DEVICE_STATUS.DISCONNECTED;
  const hasBadge = data !== undefined && showsSubscriptionBadge(data.subscriptionIndicator);

  // §15a/§15b: offline falls back to the cached payload, and to three dashes where there is no
  // cache — never to the F13 error, which would blame the Device for a connection problem.
  const metrics = data?.metrics ?? (isOnline ? null : OFFLINE_METRICS);

  // §5f — a polite live region, seeded EMPTY so opening the panel does not announce a status the
  // panel's own open announcement (§13c) already carried. Only a real change speaks, which is also
  // what keeps §9e's "no notification" true for an ordinary value refresh.
  const previousLive = useRef<QuickViewLiveState | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const ready = data !== undefined;
  useEffect(() => {
    if (!ready) return;
    const message = liveAnnouncement(previousLive.current, { statusLabel: chip.label, hasBadge });
    previousLive.current = { statusLabel: chip.label, hasBadge };
    if (message !== null) setAnnouncement(message);
  }, [ready, chip.label, hasBadge]);

  // §4e is a deliberate exception to §13a's "hovering changes nothing": a tooltip appears only where
  // the name is ACTUALLY cut off, so an untruncated name still changes nothing on hover.
  const nameRef = useRef<HTMLSpanElement>(null);
  const [truncated, setTruncated] = useState(false);
  useLayoutEffect(() => {
    const element = nameRef.current;
    setTruncated(element ? element.scrollWidth > element.clientWidth : false);
  }, [identity.primary]);

  return (
    <>
      <div className={styles.header}>
        <p className={styles.title}>Device Details</p>
        <button
          type="button"
          className={styles.viewMore}
          onClick={() => navigate(routes.device(device.id))}
        >
          View More
        </button>
      </div>

      <div className={styles.body}>
        <div className={styles.identity}>
          <p className={styles.name}>
            <Tooltip title={truncated ? identity.primary : ''}>
              <span ref={nameRef} className={styles.nameText}>
                {identity.primary}
              </span>
            </Tooltip>
            {hasBadge ? (
              <ShieldCheckIcon
                size={24}
                weight="fill"
                className={styles.badge}
                role="img"
                aria-label="Subscription active"
              />
            ) : null}
          </p>
          {/* §4c: where there is no Custom Name the Device Name has taken the line above, and is not
              repeated here. §4f: Category is never shown. */}
          {identity.secondary ? <p className={styles.model}>{identity.secondary}</p> : null}
        </div>

        <Chip intent={chip.intent} icon={chip.icon}>
          {chip.label}
        </Chip>

        {/* §5f. `role="status"` carries an implicit `aria-live="polite"`, and is what the tests
            address. Always mounted, so a change writes into an existing region rather than
            inserting one — an inserted region is announced inconsistently across screen readers. */}
        <p className={styles.announcement} role="status">
          {announcement}
        </p>

        {isError && !data && isOnline ? (
          <RegionError message={METRICS_ERROR} onRetry={() => void refetch()} />
        ) : metrics ? (
          <>
            <ul className={styles.metrics}>
              {METRIC_SLOTS.map(({ key, label }) => {
                const display = metrics.display[key];
                const indicator = metricIndicatorFor(
                  metrics.indicators[key],
                  display,
                  isDisconnected,
                );
                const { reading, unit } = splitReading(display ?? METRIC_DASH);

                return (
                  <li key={key} className={`${styles.metric} ${styles[key]}`}>
                    <p className={styles.metricLabel}>{label}</p>
                    <p className={styles.metricValue}>
                      <span className={styles.reading} data-testid="metric-reading">
                        {reading}
                      </span>
                      {unit ? (
                        <span className={styles.unit} data-testid="metric-unit">
                          {unit}
                        </span>
                      ) : null}
                      {indicator ? <MetricIndicator state={indicator} /> : null}
                    </p>
                  </li>
                );
              })}
            </ul>
            <p className={styles.lastSync}>
              <ClockClockwiseIcon size={12} aria-hidden />
              {lastSyncLine(data?.connectivity.lastSyncedAt ?? null)}
              {/* §15a/§15c — present only while the connection is down, and removed in place when it
                  returns. No Figma frame covers it (M4). */}
              {!isOnline ? (
                <span className={styles.offline}>
                  <WifiSlashIcon size={12} aria-hidden />
                  Offline
                </span>
              ) : null}
            </p>
          </>
        ) : (
          // D5b — reached only while `map-popup` is genuinely pending (no data, no error, online):
          // an errored fetch takes the branch above, and offline already fell back to `OFFLINE_METRICS`
          // (truthy) in the branch above this one. Mirrors the loaded layout's own geometry — three
          // metric columns, then the last-sync line — so the panel is the same size before and after.
          <ReadingsSkeleton />
        )}
      </div>
    </>
  );
}

/**
 * D5b's loading placeholder (Figma `2100:532901`) for the readings block only — identity and the
 * status chip come from the marker payload and are already on screen (113588's two-sources design),
 * so they are never skeletonised. Mirrors the loaded `.metrics`/`.lastSync` markup one-for-one —
 * same slots, same widths — which is what keeps the panel `DEVICE_QUICK_VIEW_SIZE` (405×309)
 * regardless of whether `map-popup` has answered yet.
 */
function ReadingsSkeleton() {
  return (
    <div aria-busy="true" aria-label="Loading readings" data-testid="quick-view-skeleton">
      <ul className={styles.metrics}>
        {METRIC_SLOTS.map(({ key }) => (
          <li key={key} className={`${styles.metric} ${styles[key]}`}>
            <span className={styles.skeletonLabel} />
            <span className={styles.skeletonReading} />
          </li>
        ))}
      </ul>
      <p className={styles.lastSync}>
        <span className={styles.skeletonSync} />
      </p>
    </div>
  );
}
